(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let navigationTimer;
  let updateTimer;
  let updateStartedAt = 0;
  let updateSequence = 0;
  let pageAnimation;
  let isNavigating = false;
  let hasRevealed = false;
  let productPositionFallback;
  const warmedProductPages = new Map();
  const pageEnterDuration = 190;
  const pageExitDuration = 140;
  const productMediaEnterDuration = 260;
  const pageEasing = 'cubic-bezier(.22, 1, .36, 1)';

  const main = () => document.getElementById('MainContent');

  const warmProductPage = (href) => {
    if (warmedProductPages.has(href)) return warmedProductPages.get(href);

    const warm = fetch(href, {
      credentials: 'same-origin',
      cache: 'force-cache',
    })
      .then((response) => response.ok ? response.text() : '')
      .then((html) => {
        if (!html) return;

        const nextPage = new DOMParser().parseFromString(html, 'text/html');
        const firstImage = nextPage.querySelector(
          '[data-product-media-container] .splide__slide img, [data-product-media-container] img'
        );
        const source = firstImage?.currentSrc || firstImage?.getAttribute('src');
        if (!source) return;

        const image = new Image();
        const sourceSet = firstImage.getAttribute('srcset');
        const sizes = firstImage.getAttribute('sizes');
        if (sourceSet) image.srcset = sourceSet;
        if (sizes) image.sizes = sizes;
        image.src = source;
        return image.decode?.().catch(() => {});
      })
      .catch(() => {});

    warmedProductPages.set(href, warm);
    return warm;
  };

  const revealPage = () => {
    if (hasRevealed && !root.classList.contains('polomi-page-entering')) return;

    window.clearTimeout(navigationTimer);
    window.clearTimeout(productPositionFallback);
    document.querySelector('.polomi-product-position-pending')?.classList.remove('polomi-product-position-pending');
    isNavigating = false;
    hasRevealed = true;
    root.classList.remove('polomi-page-leaving');

    if (reducedMotion.matches || typeof document.body?.animate !== 'function') {
      root.classList.remove('polomi-page-entering', 'polomi-product-navigation-entering');
      return;
    }

    pageAnimation?.cancel();
    const isProductNavigation = root.classList.contains('polomi-product-navigation-entering');
    root.classList.add('polomi-page-animating');
    root.classList.remove('polomi-page-entering');

    if (isProductNavigation) {
      const media = document.querySelector('[data-product-media-container]');
      const mediaVisual = media?.querySelector('.splide') || media;
      root.classList.remove('polomi-product-navigation-entering');

      if (!mediaVisual) {
        root.classList.remove('polomi-page-animating');
        return;
      }

      pageAnimation = mediaVisual.animate(
        [
          { opacity: 0 },
          { opacity: 1 },
        ],
        {
          duration: productMediaEnterDuration,
          easing: pageEasing,
          fill: 'both',
        }
      );
      pageAnimation.addEventListener('finish', () => {
        pageAnimation = null;
        root.classList.remove('polomi-page-animating');
      }, { once: true });
      return;
    }

    pageAnimation = document.body.animate(
      [
        { opacity: 0 },
        { opacity: 1 },
      ],
      {
        duration: pageEnterDuration,
        easing: pageEasing,
        fill: 'both',
      }
    );
    pageAnimation.addEventListener('finish', () => {
      pageAnimation = null;
      root.classList.remove('polomi-page-animating');
      root.classList.remove('polomi-product-navigation-entering');
    }, { once: true });
  };

  const revealWhenPositioned = () => {
    if (!document.querySelector('.polomi-product-position-pending')) {
      revealPage();
      return;
    }

    window.clearTimeout(productPositionFallback);
    productPositionFallback = window.setTimeout(revealPage, 180);
  };

  document.addEventListener('polomi:product-position-ready', revealPage);

  const beginContentUpdate = () => {
    const content = main();
    if (!content || reducedMotion.matches) return;

    window.clearTimeout(updateTimer);
    updateStartedAt = performance.now();
    updateSequence += 1;
    content.classList.add('polomi-content-updating');
    updateTimer = window.setTimeout(finishContentUpdate, 1500);
  };

  const visibleImagesReady = async (scope) => {
    const images = Array.from(scope?.querySelectorAll('img') || []).filter((image) => {
      const rect = image.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });

    await Promise.race([
      Promise.all(
        images.map(async (image) => {
          if (!image.complete) {
            await new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });
          }

          if (typeof image.decode === 'function') {
            try {
              await image.decode();
            } catch (_error) {
              // A loaded image can reject decode without preventing it from rendering.
            }
          }
        })
      ),
      new Promise((resolve) => window.setTimeout(resolve, 320)),
    ]);
  };

  async function finishContentUpdate(event) {
    const content = main();
    if (!content) return;

    window.clearTimeout(updateTimer);
    const sequence = updateSequence;
    const productRoot = event?.detail?.productRootEl || content.querySelector('[data-product-root]');
    await visibleImagesReady(productRoot);

    const minimumFadeTime = 130;
    const remainingFadeTime = Math.max(0, minimumFadeTime - (performance.now() - updateStartedAt));
    if (remainingFadeTime) {
      await new Promise((resolve) => window.setTimeout(resolve, remainingFadeTime));
    }

    if (sequence !== updateSequence) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => content.classList.remove('polomi-content-updating'));
    });
  }

  const shouldNavigate = (event, link) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download') ||
      link.hasAttribute('data-no-page-transition') ||
      link.hasAttribute('data-cart-drawer-toggle') ||
      link.hasAttribute('data-polomi-cart-trigger') ||
      link.getAttribute('href')?.trim().startsWith('#')
    ) {
      return false;
    }

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return false;

    const current = new URL(window.location.href);
    return !(
      url.pathname === current.pathname &&
      url.search === current.search &&
      url.hash
    );
  };

  const navigate = (href, options = {}) => {
    if (isNavigating) return;
    isNavigating = true;
    window.clearTimeout(navigationTimer);

    if (options.productOption) {
      warmProductPage(href);

      try {
        sessionStorage.setItem('polomi-product-navigation', 'true');
      } catch (_error) {}

      if (reducedMotion.matches || typeof document.body?.animate !== 'function') {
        window.location.assign(href);
        return;
      }

      // Leave the current product fully rendered while the next document loads.
      // The destination reveals its text immediately and fades only its gallery.
      window.location.assign(href);
      return;
    }

    if (reducedMotion.matches || typeof document.body?.animate !== 'function') {
      root.classList.add('polomi-page-leaving');
      navigationTimer = window.setTimeout(() => window.location.assign(href), pageExitDuration);
      return;
    }

    const currentOpacity = window.getComputedStyle(document.body).opacity;
    pageAnimation?.cancel();
    root.classList.add('polomi-page-animating');
    pageAnimation = document.body.animate(
      [
        { opacity: currentOpacity },
        { opacity: 0 },
      ],
      {
        duration: pageExitDuration,
        easing: pageEasing,
        fill: 'forwards',
      }
    );

    pageAnimation.addEventListener('finish', () => window.location.assign(href), { once: true });
    navigationTimer = window.setTimeout(() => window.location.assign(href), pageExitDuration + 80);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !shouldNavigate(event, link) || reducedMotion.matches) return;

    event.preventDefault();
    navigate(link.href);
  });

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.(
      '.polomi-product-canvas .polomi-product-block--app linkify-product-colors-swatch[data-handle]'
    );
    if (
      !swatch ||
      swatch.hasAttribute('data-selected') ||
      swatch.hasAttribute('data-soldout') ||
      reducedMotion.matches
    ) return;

    const handle = swatch.getAttribute('data-handle');
    if (!handle) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const prefixMatch = window.location.pathname.match(
      /^((\/[a-z0-9_-]{1,8})?(\/collections\/[a-z0-9-]+)?)\/products\//i
    );
    const prefix = prefixMatch?.[1] || '';
    navigate(`${prefix}/products/${handle}`, { productOption: true });
  }, true);

  const warmProductOption = (event) => {
    const swatch = event.target.closest?.(
      '.polomi-product-canvas .polomi-product-block--app linkify-product-colors-swatch[data-handle]'
    );
    if (!swatch || swatch.hasAttribute('data-selected') || swatch.hasAttribute('data-soldout')) return;

    const handle = swatch.getAttribute('data-handle');
    if (!handle) return;

    const prefixMatch = window.location.pathname.match(
      /^((\/[a-z0-9_-]{1,8})?(\/collections\/[a-z0-9-]+)?)\/products\//i
    );
    const prefix = prefixMatch?.[1] || '';
    warmProductPage(`${prefix}/products/${handle}`);
  };

  document.addEventListener('pointerover', warmProductOption, { passive: true });
  document.addEventListener('focusin', warmProductOption);

  document.addEventListener('change', (event) => {
    if (event.target.closest?.('[data-product-root]')) beginContentUpdate();
  }, true);

  document.addEventListener('theme:change:variant', beginContentUpdate, true);
  document.addEventListener('theme:change:option-values', beginContentUpdate, true);
  document.addEventListener('theme:variant:change', finishContentUpdate);

  const contentObserver = new MutationObserver((mutations) => {
    const content = main();
    if (!content?.classList.contains('polomi-content-updating')) return;
    if (mutations.some((mutation) => mutation.type === 'childList')) finishContentUpdate();
  });

  if (main()) contentObserver.observe(main(), { childList: true });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      hasRevealed = false;
      root.classList.add('polomi-page-entering');
    }
    revealWhenPositioned();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealWhenPositioned, { once: true });
  } else {
    revealWhenPositioned();
  }
})();
